import { useCallback, useEffect, useMemo, useState } from 'react';
import Page from 'src/components/layouts/Page';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import Button from 'src/components/atoms/Button';
import TableFilterable from 'src/components/organisms/TableFilterable';
import moment from 'moment';
import MultipleViews from 'src/components/layouts/MultipleViews';
import CancelOrderModal from 'src/components/modals/CancelOrderModal';
import OrdersGrid from 'src/components/layouts/OrdersGrid';
import OrderModal from 'src/components/modals/OrderModal';
import EmptyPage from 'src/components/molecules/EmptyPage';
import SearchBar from 'src/components/organisms/SearchBar';
import { toggleOrderModal } from 'src/slices/modals';
import { clearState, getOrders, pastOrderDetails, updateOrder } from 'src/slices/order';
import utils, { handleStatus } from '../slices/utils';

import { useNavigate } from 'react-router-dom';
import OrderStatusChanger from 'src/components/atoms/OrderStatusChanger';
import css from '../components/organisms/TableFilterable.module.scss';
import OrderStatusChangerSimple from 'src/components/organisms/OrderStatusChangerSimple';
// we're using React.useMemo here to ensure that our data isn't recreated on every render.
// accessor is the "key" in the data

export default function Orders() {
  const dispatch = useDispatch();
  const { orders, status } = useSelector((state) => state.order);

  const { permissions } = useSelector((state) => state.auth);
  const [cancelledOrder, setCancelledOrderId] = useState();
  const [showModal, setShowModal] = useState(false);
  const [view, setView] = useState('grid');

  const orderTypeSelect = [
    {
      value: () => dispatch(getOrders(params)),
      params: null,
      label: 'All',
    },
    {
      value: () => dispatch(getOrders({ ...params, current: true })),
      params: { current: true },
      label: 'Current',
    },
    {
      value: () => dispatch(getOrders({ ...params, past: true })),
      params: { past: true },
      label: 'Past',
    },
  ];
  const [orderType, setOrderType] = useState(orderTypeSelect[0]);

  const [params, setParams] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getOrders({ ...params, ...orderType.params }));
  }, [params, dispatch, orderType.params]);

  // const search = () => useCallback, [])

  const search = useCallback(
    utils.debounce((args) => dispatch(getOrders(args)), 250),
    [],
  );

  const handleDetails = (orderId) => {
    dispatch(toggleOrderModal());

    dispatch(pastOrderDetails(orderId))
      .unwrap()
      .then(() => {
        const params = new URLSearchParams(window.location.search);

        params.set('orderId', orderId);

        navigate({ search: params.toString() }, { replace: true });
      });
  };

  useEffect(() => {
    if (orders?.length) {
      const params = new URLSearchParams(window.location.search);
      const orderId = params.get('orderId');

      if (orderId?.length && orders.find((order) => order._id === orderId)) {
        dispatch(toggleOrderModal({ isOpen: true }));
        dispatch(pastOrderDetails(orderId));
      } else {
        params.delete('orderId');

        navigate({ search: params.toString() }, { replace: true });
      }
    }
  }, [orders, dispatch, navigate]);

  function handleCancel(order) {
    setShowModal(true);
    setCancelledOrderId(order);
  }

  const handleSearch = (keyword) => {
    setParams({ ...params, keyword });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Order No.',
        accessor: (row) => `#${row.orderRef}`,
      },

      {
        Header: 'Date',
        accessor: 'orderDate',
        accessor: (row) => `${moment(row.orderDate).format('MM/DD/YYYY hh:mm A')}`,
      },
      {
        Header: 'Customer',
        accessor: (row) => `${row.shopperName}`,
      },
      {
        Header: 'Subtotal',
        accessor: (row) => `$${row.merchantShare.toFixed(2)}`,
      },
      {
        Header: 'Total',
        accessor: (row) => `$${(row.merchantShare + row.tax).toFixed(2)}`,
      },
      {
        Header: ' ',
        Cell: (props) => (
          <div
            style={{
              display: 'flex',
              gap: '32px',
              justifyContent: 'flex-end',
            }}
          >
            <OrderStatusChangerSimple order={props.row.original} handleCancel={handleCancel} />

            <Button
              label={'Details'}
              size="s"
              color="normal"
              variant="secondary"
              iconLeft={'fad fa-info-circle'}
              action={() => handleDetails(props.row.original._id)}
            />
          </div>
        ),
      },
    ],

    [orders],
  );
  useEffect(() => {
    handleStatus(status.updateOrder, () => dispatch(clearState(status.updateOrder)));
  }, [status.updateOrder]);

  useEffect(() => orderType.value(), [orderType]);

  return (
    <Page>
      {permissions.read.includes('orders') ? (
        <>
          {showModal && (
            <CancelOrderModal
              closeFunction={() => setShowModal(false)}
              selectedOrder={cancelledOrder}
            />
          )}

          <MultipleViews
            // TODO: Card view default for mobile
            showControls
            defaultView="grid"
            isEmpty={orders?.length === 0}
            setView={setView}
            view={view}
            searchBar={
              <SearchBar
                dataName="order"
                onChange={(e) => handleSearch(e.target.value)}
                value={params.keyword}
                canCreate={false}
                select={
                  <div className={css['actions']}>
                    <Select
                      options={orderTypeSelect}
                      placeholder={orderType.label}
                      onChange={(option) => setOrderType(option)}
                      className={css['custom-select']}
                      value={orderType}
                    />
                  </div>
                }
              />
            }
            tableView={
              <TableFilterable
                columns={columns}
                data={orders}
                dataName="order"
                showBar={false}
                itemsSelectable={false}
                canCreate={false}
                status="orders"
              />
            }
            gridView={
              <OrdersGrid
                orders={orders}
                handleDetails={handleDetails}
                handleCancel={handleCancel}
              />
            }
            emptyView={
              <EmptyPage title="Your store doesn’t have any orders yet" iconName="fad fa-receipt" />
            }
          />
          <OrderModal />
        </>
      ) : (
        <EmptyPage
          title="You don’t have permission to view anything on this page"
          text="Ask your employer for access"
          iconName="fad fa-lock"
        />
      )}
    </Page>
  );
}
